import {
  ResponseResult,
  type SubQuestionResponseEntry,
} from '@amzn/gbsa-helios-typescript-client';

/**
 * Inputs for {@link shouldShowParentNaReasonPicker}.
 */
export interface ParentNaReasonVisibilityArgs {
  /**
   * Parent question's current result — either explicit (auditor
   * tapped NA/All) or derived from child aggregation (via
   * {@link deriveParentResult} at the caller). Only when this is NA
   * can the parent picker be visible.
   */
  readonly parentResult: ResponseResult | undefined;
  /**
   * Sub-question responses cached on the parent. Feeds the "every
   * NA child already has a reason" cascade rule.
   */
  readonly subResponses: readonly SubQuestionResponseEntry[] | undefined;
  /**
   * Predicate: does this sub-question currently have an NA reason
   * selected? Should return true when EITHER the server-side
   * `naReason` label is present OR the caller's in-flight local
   * override is. The caller composes this from the
   * `useNaReasonDraft` state map — the visibility fn stays
   * SDK-shape agnostic so it doesn't need to know about local
   * drafts.
   */
  readonly hasSubReason: (subQuestionId: string) => boolean;
}

/**
 * When should the PARENT-level NA reason picker render?
 *
 * Rules mirror `shouldShowNaReasonDropdown('main', ...)` from
 * `src/v2/shared/domain/auditClosureValidation` with one intentional
 * strictening: desktop relied on the caller to pre-gate on "parent is
 * NA-answered"; v2 pulls that guard inside so callers can render the
 * picker in the JSX branch that structurally makes sense and this
 * function decides. The visibility contract is complete on its own.
 *
 * Rule table:
 *   1. Parent isn't NA-answered → HIDE.
 *   2. No NA children (flat question, or all children non-NA) →
 *      SHOW. The parent OWNS the reason in that case.
 *   3. Some NA children exist but every one already has a reason
 *      (server label OR in-flight local override) → HIDE. The
 *      children already cover the answer; a parent picker would
 *      duplicate.
 *   4. Otherwise → SHOW. At least one NA child is missing a reason,
 *      or the parent was explicitly NA/All'd without per-child
 *      coverage yet.
 */
export function shouldShowParentNaReasonPicker(
  args: ParentNaReasonVisibilityArgs,
): boolean {
  const { parentResult, subResponses, hasSubReason } = args;

  // Rule 1 — parent picker requires an NA parent.
  if (parentResult !== ResponseResult.NA) return false;

  // Rule 2 — no NA children → parent owns the reason.
  const naSubIds = collectNaSubIds(subResponses);
  if (naSubIds.length === 0) return true;

  // Rule 3 — every NA child covered → hide parent picker (subs win).
  const allCovered = naSubIds.every((id) => hasSubReason(id));
  return !allCovered;
}

/**
 * Inputs for {@link shouldShowSubNaReasonPicker}.
 */
export interface SubNaReasonVisibilityArgs {
  /**
   * The sub-question's current result — server or optimistic. Only
   * when this is NA can the sub picker be visible.
   */
  readonly subResult: ResponseResult | undefined;
  /**
   * True if THIS sub-question already has a reason selected — server
   * label OR in-flight local override. The caller composes this from
   * `useNaReasonDraft` state; the visibility fn stays SDK-shape
   * agnostic.
   */
  readonly subHasReason: boolean;
  /**
   * Parent question's current result — used for the cascade check.
   * The cascade fires only when `parentResult === ResponseResult.NA`,
   * capturing both the "parent bulk-NA'd" and "parent NA with its
   * own reason" cases in a single condition.
   */
  readonly parentResult: ResponseResult | undefined;
  /**
   * True if ANY sibling sub-question is currently answered NA
   * (regardless of whether it has a reason picked yet). When true,
   * the per-sub workflow is active — meaning the auditor is answering
   * sub-questions individually, and the parent's NA is derived from
   * children. The cascade rule (parentResult is NA, HIDE) only fires
   * when this is false (i.e. the parent was explicitly bulk-NA'd
   * without any individual sub being NA beforehand).
   *
   * v1 parity: v1 never derived parentResult from children on the
   * frontend, so its isNaAll check only fired for explicit bulk
   * NA/All. v2 derives parentResult optimistically, so this flag
   * recreates the v1 semantics by distinguishing bulk-NA from
   * derived-NA.
   */
  readonly hasAnySiblingNa: boolean;
}

/**
 * When should a per-sub NA reason picker render?
 *
 * Rules — v2 refinement of the desktop
 * `shouldShowNaReasonDropdown('sub', ...)`:
 *
 *   1. Sub isn't NA-answered → HIDE.
 *   2. Sub already has a reason (server or local) → SHOW.
 *      "Sticky" — a picker whose value was already picked stays
 *      visible even if the parent later becomes NA with its own
 *      reason. Hiding it silently would obscure the auditor's
 *      already-made choice.
 *   3. Parent is currently NA AND no sibling has a per-sub reason
 *      → HIDE. The parent picker owns the answer (bulk NA/All
 *      scenario). But if any sibling already has a per-sub reason,
 *      the parent's NA is derived from individual children and the
 *      cascade does not apply — each sub keeps its own picker
 *      (v1 parity).
 *   4. Otherwise → SHOW.
 *
 * v2 divergence from desktop: desktop had a separate "parent has
 * naReasonId → hide" branch INDEPENDENT of parentResult. Desktop
 * maintained the invariant `parent has naReasonId ⇒ parent is NA`
 * via synchronous cascades on answer change, so the two conditions
 * were equivalent. v2's answer builders enforce the same invariant
 * on the wire (clearing naReason on non-NA answers), but existing
 * cached data or in-flight local drafts can still transiently
 * violate it — so v2 gates the cascade on
 * `parentResult === ResponseResult.NA` directly. Prevents a stale
 * parent reason from hiding a fresh sub picker.
 *
 * Rule 2 runs before rule 3 on purpose: the "sticky already-picked"
 * branch wins over the cascade. Reordering would make a sub-picker
 * vanish mid-audit as soon as the parent gained a reason, dropping
 * the auditor's already-made choice from the visible UI.
 */
export function shouldShowSubNaReasonPicker(args: SubNaReasonVisibilityArgs): boolean {
  const { subResult, subHasReason, parentResult, hasAnySiblingNa } = args;
  if (subResult !== ResponseResult.NA) return false;
  if (subHasReason) return true;
  // The cascade (parent NA → HIDE sub picker) only applies when the
  // parent's NA came from an explicit bulk NA/All tap — NOT when it
  // was derived from all children individually being NA. v1 parity:
  // v1 never derived parentResult on the frontend, so its `isNaAll`
  // check only fired for the explicit case. In v2, if any sibling
  // is currently NA (per-sub workflow is active), the parent's NA is
  // derived — skip the cascade so each sub keeps its own picker.
  if (parentResult === ResponseResult.NA && !hasAnySiblingNa) return false;
  return true;
}

/**
 * Collect the ids of sub-questions currently answered NA. Kept as
 * a helper so the outer function stays under the sonarjs cognitive-
 * complexity budget and the per-entry object-access reads clearly.
 */
function collectNaSubIds(
  entries: readonly SubQuestionResponseEntry[] | undefined,
): string[] {
  if (!entries || entries.length === 0) return [];
  const naIds: string[] = [];
  for (const entry of entries) {
    if (entry.response?.responseResult === ResponseResult.NA) {
      naIds.push(entry.subQuestionId);
    }
  }
  return naIds;
}

/**
 * v1 parity input for {@link shouldShowSubNaReasonPicker}: is ANY
 * SIBLING sub-question (an entry other than the given one) currently
 * answered NA? When true the per-sub reason workflow is active and
 * the parent NA cascade is suppressed. Pure — extracted from the
 * sub-question row so the predicate is testable without a render.
 */
export function hasSiblingNaAnswer(
  entries: readonly SubQuestionResponseEntry[] | undefined,
  excludeSubQuestionId: string,
): boolean {
  return (entries ?? []).some(
    (entry) =>
      entry.subQuestionId !== excludeSubQuestionId &&
      entry.response?.responseResult === ResponseResult.NA,
  );
}
